import PropTypes from 'prop-types';
import React, { useState } from 'react';
import { CheckCircle2, Plus } from 'lucide-react';

import EducationDialog from './EducationDialog';
import EducationDetails from './EducationDetails/EducationDetails';
import { useEntryDialog } from '../useCareerEntries';

import useAuth from 'hooks/useAuth';
import { getEducationEntries } from 'helpers/portfolioLayout';

import styles from '../CareerEntries.module.scss';
import hocStyles from '../../hoc/hoc.module.scss';
import Loader from 'components/Loader/Loader';

const {
  panel,
  dashedPanel,
  headerContainer,
  header,
  count,
  hiddenBadge,
  placeholder: placeholderStyle,
  addLink,
  addBtn,
  editWrp,
  loader,
  tick,
} = hocStyles;

// Rows shown before "Show all N".
const VISIBLE_ROWS = 4;

const META = {
  lastUpdated: 'education',
  eventName: 'freelancer-updates-education',
};

// `editor` is useCareerEntries for user.education, shared with Qualification.
const Education = ({
  user,
  isOwn,
  editor,
  isEmpty,
  placeholder,
  addRequest,
  onAddQualification,
}) => {
  const { lastUpdated, updating, updated } = useAuth();
  const [showAll, setShowAll] = useState(false);
  const { dialog, open, close } = useEntryDialog(addRequest);
  const { visibleEntries, saving, saveEntry, deleteEntry } = editor;
  const educationEntries = getEducationEntries(visibleEntries);

  const instanceIsUpdating = lastUpdated === 'education';

  const successTick = !updating &&
    instanceIsUpdating &&
    updated &&
    !dialog.open && (
      <CheckCircle2 className={tick} size={20} aria-label="Saved" />
    );

  if (!isOwn && isEmpty) {
    return null;
  }

  const showOwnerEmpty = isOwn && educationEntries.length === 0;
  const total = educationEntries.length;
  const visible = showAll
    ? educationEntries
    : educationEntries.slice(0, VISIBLE_ROWS);

  return (
    <section
      id="portfolio-education"
      className={showOwnerEmpty ? dashedPanel : panel}
    >
      <div className={headerContainer}>
        <h2 className={header}>
          Education
          {total > VISIBLE_ROWS && <span className={count}>· {total}</span>}
        </h2>
        {showOwnerEmpty ? (
          <span className={hiddenBadge}>Hidden until added</span>
        ) : (
          user &&
          isOwn && (
            <div className={editWrp}>
              {successTick}
              {instanceIsUpdating && updating && !dialog.open && (
                <Loader loading className={loader} />
              )}
              <button
                id="add-education"
                type="button"
                className={addBtn}
                onClick={() => open()}
              >
                <Plus size={14} strokeWidth={2.4} aria-hidden="true" />
                Add education
              </button>
            </div>
          )
        )}
      </div>
      {showOwnerEmpty ? (
        <p className={placeholderStyle}>
          {placeholder}{' '}
          <button type="button" className={addLink} onClick={() => open()}>
            Add education
          </button>
        </p>
      ) : (
        <div className={styles.compactList}>
          {visible.map((educationItem, index) => (
            <EducationDetails
              key={
                educationItem.id
                  ? `edDetails${educationItem.id}`
                  : `edDetails${index}`
              }
              education={educationItem}
              isOwn={isOwn}
              user={user}
              onEdit={() => open(educationItem)}
            />
          ))}
          {total > VISIBLE_ROWS && (
            <button
              type="button"
              className={styles.showAll}
              onClick={() => setShowAll(!showAll)}
              aria-expanded={showAll}
            >
              {showAll ? 'Show fewer' : `Show all ${total}`}
            </button>
          )}
        </div>
      )}

      {isOwn && dialog.key > 0 && (
        <EducationDialog
          key={dialog.key}
          show={dialog.open}
          education={dialog.entry}
          saving={saving}
          onSave={(education) => saveEntry(dialog.entry, education, META)}
          onDelete={() => {
            close();
            deleteEntry(
              dialog.entry,
              `Education deleted: ${
                dialog.entry.qualification || dialog.entry.organization
              }`,
              META
            );
          }}
          onClose={close}
          onAddQualification={onAddQualification}
        />
      )}
    </section>
  );
};

Education.propTypes = {
  user: PropTypes.object,
  isOwn: PropTypes.bool,
  editor: PropTypes.shape({
    visibleEntries: PropTypes.array.isRequired,
    saving: PropTypes.bool,
    saveEntry: PropTypes.func.isRequired,
    deleteEntry: PropTypes.func.isRequired,
  }).isRequired,
  isEmpty: PropTypes.bool,
  placeholder: PropTypes.string,
  addRequest: PropTypes.number,
  onAddQualification: PropTypes.func,
};

export default Education;
